import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

// A tab's optional tone: the bar beside its label and its text color, on every tab, not
// only the open one. The white pill still marks which tab is open. The Outstanding List
// uses these for its two review lists, matching the bars on their rows: gold for WPC
// Review, blue for Dealer Review. Off the white pill the text sits on the gray track,
// where gold-dark is too faint (3.7:1), so unselected gold text is gold-800 (5.6:1).
const TONES = {
  gold: {
    bar: 'bg-gold-dark',
    active: 'text-gold-dark',
    inactive: 'text-gold-800 hover:text-gold-900',
  },
  blue: {
    bar: 'bg-blue-dark',
    active: 'text-blue-dark',
    inactive: 'text-blue-dark hover:text-blue-950',
  },
}

/**
 *
 * SegmentedTabs
 *
 * The app's segmented control: a small set of options where every option is worth
 * reading at a glance and switching is the main thing anyone does.
 *
 * Styled to match the Outstanding List / RO Intake / Billing group in `DealerHeader`,
 * which is the same control doing the same job one level up. Keep the two in step.
 *
 * Extracted from the Users page (WARP-939) when RO Intake's Log ROs screen needed the
 * same control for its three entry methods (WARP-942).
 */
const SegmentedTabs = ({ activeTab, ariaLabel, onChange, panelId, tabs }) => {
  const tabRefs = useRef({})

  // Arrow keys move between tabs, which is what a tablist is expected to do. Without
  // it, a keyboard user has to tab out of the control and back in to reach the other
  // side.
  const handleKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

    event.preventDefault()
    const index = tabs.findIndex((tab) => tab.id === activeTab)
    const offset = event.key === 'ArrowRight' ? 1 : -1
    const next = tabs[(index + offset + tabs.length) % tabs.length]

    if (next.disabled) return

    onChange(next.id)
    tabRefs.current[next.id]?.focus()
  }

  const renderTab = (tab) => {
    const isActive = tab.id === activeTab

    return (
      <button
        aria-controls={panelId}
        aria-selected={isActive}
        className={mergeClassNames(
          'rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-all',
          // focus-visible, not focus: the reference group has no ring at all, and a halo
          // left behind by a mouse click would be the one way these two differ. Keyboard
          // users still get it, which is who it is for.
          'focus-visible:ring-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
          isActive
            ? mergeClassNames('bg-white text-blue-800 shadow-sm', TONES[tab.tone]?.active)
            : mergeClassNames(
                // charcoal-600, not 400: 400 is 3.0:1 on the gray track, too faint for 12px
                // text and too faint to tell the tabs apart; 600 is 5.3:1.
                'text-charcoal-600 hover:text-charcoal-900',
                TONES[tab.tone]?.inactive,
              ),
          (tab.tone || tab.swatch) && 'inline-flex items-center',
          tab.disabled && 'text-charcoal-300 hover:text-charcoal-300 cursor-not-allowed',
        )}
        disabled={tab.disabled}
        key={tab.id}
        onClick={() => onChange(tab.id)}
        onKeyDown={handleKeyDown}
        ref={(element) => {
          tabRefs.current[tab.id] = element
        }}
        role="tab"
        // Only the selected tab is in the tab order; the arrow keys reach the rest.
        tabIndex={isActive ? 0 : -1}
        type="button"
      >
        {(tab.tone || tab.swatch) && (
          <i
            aria-hidden
            className={mergeClassNames(
              'mr-2 h-3.5 w-1 shrink-0 rounded-full',
              TONES[tab.tone]?.bar,
            )}
            style={!tab.tone && tab.swatch ? { backgroundColor: tab.swatch } : undefined}
          />
        )}
        {tab.label}
      </button>
    )
  }

  const trackClassName = 'bg-charcoal-100 inline-flex flex-wrap items-center rounded-lg p-1'

  // Tabs split into groups wherever a tab names a new one (`groupLabel`). Without any,
  // the control is one track, as everywhere else in the app.
  const groups = tabs.reduce((all, tab) => {
    if (tab.groupLabel || all.length === 0) all.push({ label: tab.groupLabel, tabs: [] })
    all[all.length - 1].tabs.push(tab)
    return all
  }, [])

  if (groups.length === 1 && !groups[0].label) {
    return (
      <div aria-label={ariaLabel} className={trackClassName} role="tablist">
        {tabs.map(renderTab)}
      </div>
    )
  }

  // Grouped: a track per group, each captioned, side by side. Still one tablist, so the
  // arrow keys move across every tab; the wrappers are presentational.
  return (
    <div
      aria-label={ariaLabel}
      className="flex flex-wrap items-end gap-x-4 gap-y-2"
      role="tablist"
    >
      {groups.map((group) => (
        <div className="flex flex-col gap-1" key={group.tabs[0].id} role="none">
          {group.label && (
            <span
              aria-hidden
              className="text-charcoal-500 px-1 text-[10px] font-bold uppercase tracking-widest"
            >
              {group.label}
            </span>
          )}
          <div className={trackClassName} role="none">
            {group.tabs.map(renderTab)}
          </div>
        </div>
      ))}
    </div>
  )
}

SegmentedTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  panelId: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      // Starts a new group of tabs, captioned with this name, in a track of its own.
      groupLabel: PropTypes.string,
      id: PropTypes.string.isRequired,
      // Text, or text with counts beside it (the Outstanding List tabs).
      label: PropTypes.node.isRequired,
      tone: PropTypes.oneOf(Object.keys(TONES)),
      // A bar color only, for a tab without a tone: e.g. a status's dashboard swatch.
      swatch: PropTypes.string,
    }),
  ).isRequired,
}

export default SegmentedTabs
